import type { MouseEventHandler } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import Exporter, { EXCEL_EXPORT_MAX_ROW_COUNT, PDF_EXPORT_MAX_ROW_COUNT } from 'components/stateless/Exporter/Exporter';
import Pagination from 'components/stateless/Pagination/Pagination';
import SearchBorder from 'components/stateless/SearchForm/SearchBorder';
import SearchDatePicker from 'components/stateless/SearchForm/SearchDatePicker';
import SearchInput from 'components/stateless/SearchForm/SearchInput';
import SearchLabel from 'components/stateless/SearchForm/SearchLabel';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import TableHeader from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import TdLink from 'components/stateless/Table/TdLink';
import Tr from 'components/stateless/Table/Tr';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { ROUTES_FI } from 'constants/routes/financier';
import type { PageableType } from 'hooks/usePageable';
import type Pageable from 'models/Pageable';
import type { AnchorDealerVOModel } from 'models/vo/AnchorDealerVO';
import type { PDFExporterProps } from 'utils/exportFile/exportPDF.d';
import type { AnchorDealerListRequest } from 'utils/http/api/anchor/anchor-dealers/request';
import { requestFinancierAnchorDealerList } from 'utils/http/api/financier/anchor-dealers';
import useModal from 'utils/modal/useModal';
import type { ColumnOption, ExportSpreadSheetProps } from 'utils/spreadSheet/types';
import { tableValueManage } from 'utils/valueManager/ValueManager';

interface FinancierAnchorDealerRegistrationListCompletedTabProps {
  anchorDealerList: Pageable<AnchorDealerVOModel[]>;
  pageable: PageableType;
  paginate(page: number, sizePerPage: number): void;
  handleClickSearch: MouseEventHandler<HTMLButtonElement>;
}

function FinancierAnchorDealerRegistrationListCompletedTab({
  anchorDealerList,
  pageable,
  paginate,
  handleClickSearch,
}: FinancierAnchorDealerRegistrationListCompletedTabProps) {
  const { register, reset, control, getValues } = useFormContext<AnchorDealerListRequest>();
  const { t } = useTranslation(['format']);
  const modal = useModal();

  const getRegistrationConfirmedTableHeaders = (isPDFHeader = false) => {
    const headers = [
      {
        headerText: t('text:Registration_Date'),
      },
      {
        headerText: t('text:Uploaded_Dealer_Name'),
      },
      {
        headerText: t('text:Dealer_Tax_Code'),
      },
      {
        headerText: t('text:Associated_Anchor_Name'),
      },
      {
        headerText: t('text:Legal_Representative_Name'),
      },
      {
        headerText: t('text:Legal_Representative_Email'),
      },
      {
        headerText: t('text:Telephone'),
      },
      {
        headerText: t('text:Registered_Office_Address'),
      },
    ];

    // isPDFHeader가 true이면 마지막 항목을 제외합니다.
    return isPDFHeader ? headers : [...headers, { headerText: '', colWidths: 50 }];
  };

  const renderTableBodyResult = (data: AnchorDealerVOModel[] | undefined, isPdf = false) => {
    return data?.map((item, index) => (
      <Tr key={index} className={isPdf ? 'virtual-table-row' : ''}>
        <Td data={item.createdDateTime} format="datetime" />
        <Td data={item.name} />
        <Td data={item.taxCode} />
        <Td data={item.anchorName} />
        <Td data={item.representativeName} />
        <Td data={item.representativeEmail} />
        <Td data={item.telephone} />
        <Td data={item.address} />
        {!isPdf && <TdLink path={ROUTES_FI.MANAGE_DEALER.REGISTRATION_DETAIL_BUILD_PATH(item.id)} />}
      </Tr>
    ));
  };

  const onClickConfirmedViewExportButton = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();

    const CONFIRMED_VIEW_EXCEL_COLUMNS: ColumnOption<AnchorDealerVOModel>[] = [
      {
        header: t('text:Registration_Date'),
        key: 'createdDateTime',
      },
      {
        header: t('text:Dealer_Name'),
        key: 'name',
      },
      {
        header: t('text:Dealer_Tax_Code'),
        key: 'taxCode',
      },
      {
        header: t('text:Associated_Anchor_Name'),
        key: 'anchorName',
      },
      {
        header: t('text:Legal_Representative_Name'),
        key: 'representativeName',
      },
      {
        header: t('text:Legal_Representative_Email'),
        key: 'representativeEmail',
      },
      {
        header: t('text:Telephone'),
        key: 'telephone',
      },
      {
        header: t('text:Registered_Office_Address'),
        key: 'address',
      },
    ];

    const confirmedSearchFormValues = getValues();

    try {
      const confirmedAnchorDealerExcelPage = await requestFinancierAnchorDealerList(
        0,
        EXCEL_EXPORT_MAX_ROW_COUNT,
        confirmedSearchFormValues,
      );

      const jsonArrayData = confirmedAnchorDealerExcelPage.content.map(confirmedAnchorDealerData => ({
        createdDateTime: tableValueManage(
          confirmedAnchorDealerData.createdDateTime,
          t('format:datetime', {
            value: confirmedAnchorDealerData.createdDateTime,
            key: 'datetime',
          }),
        ),
        name: tableValueManage(confirmedAnchorDealerData.name),
        taxCode: tableValueManage(confirmedAnchorDealerData.taxCode),
        address: tableValueManage(confirmedAnchorDealerData.address),
        representativeName: tableValueManage(confirmedAnchorDealerData.representativeName),
        representativeEmail: tableValueManage(confirmedAnchorDealerData.representativeEmail),
        telephone: tableValueManage(confirmedAnchorDealerData.telephone),
        anchorName: tableValueManage(confirmedAnchorDealerData.anchorName),
      }));

      const excelExporterProps: ExportSpreadSheetProps<AnchorDealerVOModel> = {
        jsonArrayData: jsonArrayData,
        columns: CONFIRMED_VIEW_EXCEL_COLUMNS,
        options: {
          rowHeight: [{ position: 1, height: 30 }],
        },
      };

      const confirmedAnchorDealerPDFPage = confirmedAnchorDealerExcelPage.content.slice(0, PDF_EXPORT_MAX_ROW_COUNT);

      const pdfExporterProps: PDFExporterProps = {
        tableHeaders: getRegistrationConfirmedTableHeaders(true),
        tableBody: renderTableBodyResult(confirmedAnchorDealerPDFPage, true),
      };

      modal.show(<Exporter spreadSheetExporterProps={excelExporterProps} pdfExporterProps={pdfExporterProps} />, {
        title: t('text:Export_File'),
        closeBtnText: t('text:Close'),
      });
    } catch (error) {
      modal.show(error);
    }
  };

  return (
    <>
      <div className="content-area">
        <SectionTitle title={t('text:Search')}>
          <Button
            variant={ButtonVariantEnum.OUTLINED}
            color={ButtonColorEnum.SECONDARY}
            onClick={() => {
              reset({});
            }}
          >
            {t('text:Remove_Filter')}
          </Button>
        </SectionTitle>
        <form>
          <SearchBorder>
            <div className="row">
              <SearchLabel label={t('text:Registration_Date')} />
              <SearchDatePicker placeholder={t('text:from')} name="fromDate" control={control} />
              <SearchDatePicker placeholder={t('text:to')} name="toDate" control={control} />
              <SearchLabel label={t('text:Uploaded_Dealer_Name')} />
              <SearchInput name="name" ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Uploaded_Dealer_Tax_Code')} />
              <SearchInput name="taxCode" ref={register} />
              <SearchLabel label={t('text:Associated_Anchor_Name')} />
              <SearchInput name="anchorClientName" ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Legal_Representative_Name')} />
              <SearchInput name="representativeName" ref={register} />
              <SearchLabel label={t('text:Legal_Representative_Email')} />
              <SearchInput name="representativeEmail" ref={register} />
            </div>
            <div className="row">
              <SearchLabel label={t('text:Telephone')} />
              <SearchInput name="telephone" ref={register} />
              <SearchLabel label={t('text:Registered_Office_Address')} />
              <SearchInput name="address" ref={register} />
            </div>
          </SearchBorder>
          <div className="flex-center mt-4">
            <Button size={ButtonSizeEnum.LG} onClick={handleClickSearch}>
              {t('text:Search')}
            </Button>
          </div>
        </form>
      </div>
      <div className="division-border" />
      <div className="content-area">
        <SectionTitle title={t('text:Result')} />
        <div className="d-flex mb-2">
          <p className="total-data me-auto">
            {t('text:Total')} : {anchorDealerList.totalElements}
          </p>
          <Button onClick={onClickConfirmedViewExportButton} variant={ButtonVariantEnum.OUTLINED}>
            {t('text:Export')}
          </Button>
        </div>
        <TableBorder>
          <TableHeader header={getRegistrationConfirmedTableHeaders()} />
          <TableBody numOfCol={getRegistrationConfirmedTableHeaders().length}>
            {renderTableBodyResult(anchorDealerList?.content)}
          </TableBody>
        </TableBorder>
        <Pagination pageable={pageable} paginate={paginate} />
      </div>
    </>
  );
}

export default FinancierAnchorDealerRegistrationListCompletedTab;
